import type { RecordModel } from 'pocketbase';
import { pb } from './pocketbase';
import { realtimeSubscribe } from './realtime';

export type SubmissionRecord = RecordModel & {
  payload?: Record<string, unknown>;
  client_id?: string;
  submitted_at?: string;
  synced_at?: string;
  device?: string;
  layout?: string;
  expand?: {
    device?: { name?: string };
    layout?: { name?: string };
  };
};

export type SubmissionFilters = {
  query?: string;
  from?: string;
  to?: string;
  device?: string;
};

function filterString(filters: SubmissionFilters): string {
  const parts: string[] = [];
  if (filters.from) parts.push(`submitted_at >= "${new Date(filters.from).toISOString()}"`);
  if (filters.to) parts.push(`submitted_at <= "${new Date(filters.to).toISOString()}"`);
  if (filters.device) parts.push(`device = "${filters.device}"`);
  const needle = filters.query?.trim();
  if (needle) {
    const safe = needle.replace(/"/g, '');
    parts.push(`(client_id ~ "${safe}" || payload ~ "${safe}")`);
  }
  return parts.join(' && ');
}

export async function listSubmissions(filters: SubmissionFilters = {}): Promise<SubmissionRecord[]> {
  const records = await pb.collection('submissions').getFullList({
    sort: '-submitted_at',
    filter: filterString(filters),
    expand: 'device,layout'
  });
  return records as SubmissionRecord[];
}

export function subscribeSubmissions(reload: () => void): () => void {
  let active = true;
  const stop = realtimeSubscribe('submissions', () => {
    if (active) reload();
  });
  return () => {
    active = false;
    stop();
  };
}

/** Submissions are deletable by admin+ (WRITE rule) — the pilot erasure minimum. */
export async function deleteSubmission(id: string): Promise<void> {
  await pb.collection('submissions').delete(id);
}

export async function deleteSubmissions(ids: string[]): Promise<void> {
  for (const id of ids) await deleteSubmission(id).catch(() => {});
}

/** Human summary derived from common payload fields (name/summary/email). */
export function submissionSummary(record: SubmissionRecord): string {
  const payload = record.payload ?? {};
  const direct = ['name', 'full_name', 'fullName', 'summary', 'email'];
  for (const key of direct) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  const first = typeof payload.first_name === 'string' ? payload.first_name : typeof payload.firstName === 'string' ? payload.firstName : '';
  const last = typeof payload.last_name === 'string' ? payload.last_name : typeof payload.lastName === 'string' ? payload.lastName : '';
  const combined = `${first} ${last}`.trim();
  if (combined) return combined;
  const keys = Object.keys(payload);
  return keys.length ? keys.slice(0, 3).join(', ') : '(empty)';
}

export function hasConsent(record: SubmissionRecord): boolean {
  const payload = record.payload ?? {};
  return payload.consent === true || payload.consent_given === true || payload.consented === true;
}

export function hasPhoto(record: SubmissionRecord): boolean {
  const payload = record.payload ?? {};
  return typeof payload.photo === 'string' || typeof payload.photo_url === 'string' || payload.photo != null;
}

/** CSV that respects the active filters (built from the already-filtered rows). */
export function toCsv(rows: SubmissionRecord[]): string {
  const header = ['submitted_at', 'client_id', 'device', 'layout', 'consent', 'summary', 'payload'];
  const escape = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const lines = rows.map((row) =>
    [
      row.submitted_at ?? '',
      row.client_id ?? '',
      row.expand?.device?.name ?? row.device ?? '',
      row.expand?.layout?.name ?? row.layout ?? '',
      hasConsent(row) ? 'yes' : 'no',
      submissionSummary(row),
      JSON.stringify(row.payload ?? {})
    ]
      .map(escape)
      .join(',')
  );
  return [header.join(','), ...lines].join('\n');
}
